import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, FileText, ClipboardList, AlertOctagon, GitBranch, GraduationCap, AlertTriangle, Users, Beaker, Clock, ExternalLink, Flag, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_META = {
  device: { label: "Device Projects", icon: Beaker, testId: "type-device" },
  capa: { label: "CAPA", icon: ClipboardList, testId: "type-capa" },
  deviation: { label: "Deviations", icon: AlertOctagon, testId: "type-deviation" },
  change: { label: "Change Control", icon: GitBranch, testId: "type-change" },
  document: { label: "Documents", icon: FileText, testId: "type-document" },
  training_assignment: { label: "Training", icon: GraduationCap, testId: "type-training" },
  risk: { label: "Risk (ISO 14971)", icon: AlertTriangle, testId: "type-risk" },
  codesign_session: { label: "Co-Design Sessions", icon: Users, testId: "type-codesign" },
};

const ID_KEY = {
  device: "project_id", capa: "capa_id", deviation: "deviation_id",
  change: "change_id", document: "document_id", training_assignment: "assignment_id",
  risk: "risk_id", codesign_session: "session_id",
};

const TITLE_KEY = {
  device: "device_name", capa: "title", deviation: "title", change: "title",
  document: "title", training_assignment: "course_id", risk: "hazard",
  codesign_session: "title",
};

export default function InspectionPortal() {
  const { token } = useParams();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);

  // Findings
  const [findings, setFindings] = useState([]);
  const [findingOpen, setFindingOpen] = useState(false);
  const [fForm, setFForm] = useState({ title: "", description: "", severity: "Medium", recommendation: "" });
  const [filing, setFiling] = useState(false);

  const call = (path) => axios.get(`${API}/inspection/${token}${path}`).then((r) => r.data);
  const post = (path, body) => axios.post(`${API}/inspection/${token}${path}`, body).then((r) => r.data);

  // Memoized entity-type tabs — avoids re-filtering scope_types on every render.
  const visibleScopeTypes = useMemo(
    () => (overview?.scope_types || []).filter((t) => TYPE_META[t]),
    [overview],
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await call("/overview");
        setOverview(data);
        const f = await call("/findings");
        setFindings(f);
      } catch (e) {
        setError(e.response?.data?.detail || "Cannot access inspection portal");
      }
    })();
  }, [token]);

  const loadList = async (type) => {
    setActiveType(type);
    setDetail(null);
    // Clear stale list rows immediately — otherwise the interim render iterates the
    // previous type's rows keyed by the NEW type's ID_KEY (undefined), producing
    // "each child needs a unique key" warnings on tab switch.
    setList([]);
    try {
      const data = await call(`/list/${type}`);
      setList(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Load failed");
    }
  };

  const loadDetail = async (type, id) => {
    const data = await call(`/entity/${type}/${id}`);
    setDetail(data);
  };

  const openFilingDialog = () => {
    // Prefill link if a detail is selected
    setFForm({ title: "", description: "", severity: "Medium", recommendation: "" });
    setFindingOpen(true);
  };

  const fileFinding = async () => {
    if (!fForm.title || !fForm.description) {
      toast.error("Title and description required");
      return;
    }
    setFiling(true);
    try {
      const linkPayload = detail && activeType ? {
        linked_entity_type: activeType,
        linked_entity_id: detail.entity[ID_KEY[activeType]],
      } : {};
      const created = await post("/findings", { ...fForm, ...linkPayload });
      toast.success("Finding filed — Deviation opened in workspace");
      setFindings([created, ...findings]);
      setFindingOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to file finding");
    } finally {
      setFiling(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full vq-card p-8 text-center bg-white">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mb-2">Access denied</h1>
          <p className="text-slate-600" data-testid="insp-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Auditor banner */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white rounded flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none">VitaQMS — Inspection Mode</div>
              <div className="text-[10px] font-mono text-slate-400 mt-0.5">READ-ONLY AUDITOR PORTAL</div>
            </div>
          </div>
          <div className="text-right text-xs font-mono text-slate-300">
            <div><Clock className="w-3 h-3 inline mr-1" />Expires {new Date(overview.expires_at).toLocaleString()}</div>
            <div className="mt-1">Issued by {overview.issued_by}</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 lg:p-10">
        {/* Session info + File Finding CTA */}
        <div className="vq-card p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Info label="Inspection" value={overview.label} />
            <Info label="Inspector" value={overview.inspector_name || "—"} />
            <Info label="Organization" value={overview.inspector_organization || "—"} />
            <Info label="Issued at" value={new Date(overview.issued_at).toLocaleString()} />
          </div>
          <div className="mt-6 pt-6 border-t border-slate-200 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap gap-3">
              {Object.entries(overview.compliance_evidence).map(([k, v]) => v && (
                <span key={k} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                  ✓ {k.replace(/_/g, " ")}
                </span>
              ))}
            </div>
            <Button onClick={openFilingDialog} data-testid="insp-file-finding-btn"
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              <Flag className="w-4 h-4 mr-2" /> File a Finding
            </Button>
          </div>
          {findings.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="vq-overline mb-2">Findings filed this inspection ({findings.length})</div>
              <div className="space-y-2">
                {findings.map((f) => (
                  <div key={f.finding_id} data-testid={`insp-finding-${f.finding_id}`}
                       className="border border-slate-200 rounded p-3 flex items-center gap-3 text-sm">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono border ${
                      f.severity === "Critical" ? "bg-red-100 text-red-800 border-red-300" :
                      f.severity === "High" ? "bg-amber-100 text-amber-800 border-amber-300" :
                      "bg-slate-100 text-slate-700 border-slate-200"
                    }`}>{f.severity}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{f.title}</div>
                      <div className="text-[10px] font-mono text-slate-500 truncate">
                        {f.finding_id} → Deviation {f.deviation_id}
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 shrink-0">{new Date(f.filed_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Counts grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
          {Object.entries(overview.counts).map(([k, v]) => (
            <div key={k} className="vq-card p-4" data-testid={`insp-count-${k}`}>
              <div className="vq-overline text-[9px] mb-1">{k.replace(/_/g, " ")}</div>
              <div className="font-display font-extrabold text-2xl text-slate-900">{v}</div>
            </div>
          ))}
        </div>

        {/* Entity type buttons */}
        <div className="vq-overline mb-3">Browse compliance evidence</div>
        <div className="flex flex-wrap gap-2 mb-8">
          {visibleScopeTypes.map((t) => {
            const M = TYPE_META[t];
            return (
              <button key={t} data-testid={`insp-tab-${t}`}
                      onClick={() => loadList(t)}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
                        activeType === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"
                      }`}>
                <M.icon className="w-4 h-4" /> {M.label}
              </button>
            );
          })}
        </div>

        {/* List + Detail */}
        {activeType && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 vq-card overflow-hidden">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">
                {TYPE_META[activeType].label} ({list.length})
              </div>
              <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                {list.map((item) => {
                  const id = item[ID_KEY[activeType]];
                  return (
                    <button key={id} data-testid={`insp-item-${id}`}
                            onClick={() => loadDetail(activeType, id)}
                            className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${detail?.entity?.[ID_KEY[activeType]] === id ? "bg-slate-100" : ""}`}>
                      <div className="font-mono text-[10px] text-slate-500">{id}</div>
                      <div className="font-semibold text-sm text-slate-900 truncate">{item[TITLE_KEY[activeType]] || "—"}</div>
                      {item.state && <div className="text-[10px] font-mono text-slate-500 mt-1">{item.state}</div>}
                    </button>
                  );
                })}
                {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No records.</div>}
              </div>
            </div>

            <div className="lg:col-span-3">
              {detail ? (
                <>
                  <div className="vq-card p-6 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 border border-emerald-200 bg-emerald-50 px-2 py-0.5 rounded">
                        Read-only evidence
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-xl text-slate-900 mb-3">
                      {detail.entity[TITLE_KEY[activeType]] || detail.entity[ID_KEY[activeType]]}
                    </h2>
                    <pre className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded p-4 max-h-96 overflow-y-auto whitespace-pre-wrap" data-testid="insp-entity-json">
                      {JSON.stringify(detail.entity, null, 2)}
                    </pre>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="vq-card p-5">
                      <h3 className="font-display font-bold text-sm text-slate-900 mb-3">
                        Audit Trail (ALCOA+) — {detail.audit_trail.length}
                      </h3>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {detail.audit_trail.map((e) => (
                          <div key={e.audit_id} className="border-l-2 border-slate-200 pl-3 py-1 text-xs">
                            <div className="font-mono text-[10px] uppercase tracking-wider text-[#0055FF]">{e.action}</div>
                            <div className="text-slate-700">{e.user_email}</div>
                            {e.reason && <div className="italic text-slate-500">&ldquo;{e.reason}&rdquo;</div>}
                            <div className="font-mono text-[9px] text-slate-400">{new Date(e.timestamp).toLocaleString()}</div>
                          </div>
                        ))}
                        {detail.audit_trail.length === 0 && <div className="text-xs text-slate-500">No events.</div>}
                      </div>
                    </div>
                    <div className="vq-card p-5">
                      <h3 className="font-display font-bold text-sm text-slate-900 mb-3">
                        E-Signatures — {detail.signatures.length}
                      </h3>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {detail.signatures.map((s) => (
                          <div key={s.signature_id} className="border border-slate-200 rounded p-2 text-xs">
                            <div className="flex justify-between mb-1">
                              <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-700">{s.meaning}</span>
                              <span className="font-mono text-[9px] text-slate-400">{new Date(s.signed_at).toLocaleString()}</span>
                            </div>
                            <div className="font-semibold text-slate-900">{s.signer_email}</div>
                            <div className="italic text-slate-600">&ldquo;{s.reason}&rdquo;</div>
                            <div className="font-mono text-[9px] text-slate-400 mt-1">
                              21 CFR Part 11: {s.cfr_part_11_compliant ? "✓" : "✗"} • EU Annex 11: {s.eu_annex_11_compliant ? "✓" : "✗"}
                              {s.consumed_at && ` • Consumed ${new Date(s.consumed_at).toLocaleString()}`}
                            </div>
                          </div>
                        ))}
                        {detail.signatures.length === 0 && <div className="text-xs text-slate-500">No signatures.</div>}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="vq-card p-10 text-center text-slate-500">Select a record to view compliance evidence.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-7xl mx-auto px-6 py-6 text-xs font-mono text-slate-500 text-center">
          VitaQMS Inspection Portal • All access is logged • Token: {token.slice(0, 12)}…
        </div>
      </footer>

      {/* File Finding dialog */}
      <Dialog open={findingOpen} onOpenChange={setFindingOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="insp-finding-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">File an inspection finding</DialogTitle>
            <DialogDescription>
              This finding will open a Deviation in the audited organization&apos;s workspace with your identity and context attached.
              {detail && activeType && (
                <span className="block mt-2 text-xs font-mono text-slate-700">
                  Linked to: {activeType}/{detail.entity[ID_KEY[activeType]]}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Title *</Label>
              <Input data-testid="insp-finding-title" value={fForm.title}
                     onChange={(e) => setFForm({ ...fForm, title: e.target.value })}
                     className="mt-1.5" placeholder="e.g. Missing effectiveness signature on CAPA-047" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Description *</Label>
              <Textarea data-testid="insp-finding-desc" value={fForm.description}
                        onChange={(e) => setFForm({ ...fForm, description: e.target.value })}
                        rows={4} className="mt-1.5"
                        placeholder="Describe the observation with references to 21 CFR / ISO 13485 clauses where applicable." />
            </div>
            <div>
              <Label className="text-xs font-semibold">Severity</Label>
              <Select value={fForm.severity} onValueChange={(v) => setFForm({ ...fForm, severity: v })}>
                <SelectTrigger className="mt-1.5" data-testid="insp-finding-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{["Low", "Medium", "High", "Critical"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Recommendation (optional)</Label>
              <Textarea data-testid="insp-finding-rec" value={fForm.recommendation}
                        onChange={(e) => setFForm({ ...fForm, recommendation: e.target.value })}
                        rows={3} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFindingOpen(false)}>Cancel</Button>
            <Button onClick={fileFinding} disabled={filing} data-testid="insp-finding-submit"
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              <Send className="w-4 h-4 mr-2" /> {filing ? "Filing…" : "File finding"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="vq-overline mb-1">{label}</div>
      <div className="font-semibold text-slate-900">{value}</div>
    </div>
  );
}
